import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { 
    Shield,
    LayoutDashboard,
    Users,
    Building2,
    LogOut,
    Menu,
    X,
    Smartphone,
    Bot,
    BookOpen,
    LifeBuoy,
    CloudCog,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';

export default function SuperAdminLayout({ children }) {
    const location = useLocation();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [user, setUser] = useState(null);
    const [authed, setAuthed] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem('superadmin_token');
        const userData = localStorage.getItem('superadmin_user');
        
        if (!token) {
            navigate('/superadmin/login', { replace: true });
            return;
        }
        
        if (userData) {
            try {
                setUser(JSON.parse(userData));
            } catch (e) {
                localStorage.removeItem('superadmin_token');
                localStorage.removeItem('superadmin_user');
                navigate('/superadmin/login', { replace: true });
                return;
            }
        }
        setAuthed(true);
    }, [navigate]);

    const handleLogout = () => {
        localStorage.removeItem('superadmin_token');
        localStorage.removeItem('superadmin_user');
        navigate('/superadmin/login', { replace: true });
    };

    // Jangan render layout sampai auth selesai dicek
    if (!authed) {
        return null;
    }

    const navItems = [
        { path: '/superadmin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
        { path: '/superadmin/users', icon: Users, label: 'User Manager' },
        { path: '/superadmin/workspaces', icon: Building2, label: 'Workspace Manager' },
        { path: '/superadmin/companies', icon: Building2, label: 'Company Manager' },
        { path: '/superadmin/mobile-tokens', icon: Smartphone, label: 'Mobile API Tokens' },
        { path: '/superadmin/openclaw-tokens', icon: CloudCog, label: 'OpenClaw API' },
        { path: '/superadmin/ai-agents', icon: Bot, label: 'AI Agents' },
        { path: '/superadmin/api-docs', icon: BookOpen, label: 'API Documentation' },
        { path: '/superadmin/user-docs', icon: LifeBuoy, label: 'User Documentation' },
    ];

    const isActive = (path, exact = false) => {
        if (exact) {
            return location.pathname === path;
        }
        return location.pathname.startsWith(path);
    };

    return (
        <div className="min-h-screen bg-slate-900 flex">
            {/* Sidebar */}
            <aside className={cn(
                "fixed inset-y-0 left-0 z-50 flex flex-col bg-slate-800 border-r border-slate-700 transition-all duration-300",
                sidebarOpen ? "w-64" : "w-16"
            )}>
                {/* Logo */}
                <div className="h-16 flex items-center justify-between px-4 border-b border-slate-700">
                    {sidebarOpen && (
                        <div className="flex items-center gap-2">
                            <img
                                src="/assets/logo-white.png"
                                alt="TaskGlory"
                                className="h-6 w-auto"
                            />
                        </div>
                    )}
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                        className="text-slate-400 hover:text-white hover:bg-slate-700"
                    >
                        {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </Button>
                </div>

                {/* Navigation */}
                <nav className="flex-1 p-4 space-y-2">
                    {navItems.map((item) => (
                        <Link
                            key={item.path}
                            to={item.path}
                            className={cn(
                                "flex items-center gap-3 px-3 py-2 rounded-lg transition-colors",
                                isActive(item.path, item.exact)
                                    ? "bg-amber-500/20 text-amber-500"
                                    : "text-slate-400 hover:text-white hover:bg-slate-700"
                            )}
                        >
                            <item.icon className="w-5 h-5 flex-shrink-0" />
                            {sidebarOpen && <span>{item.label}</span>}
                        </Link>
                    ))}
                </nav>

                {/* User & Logout */}
                <div className="p-4 border-t border-slate-700">
                    {sidebarOpen && user && (
                        <div className="mb-3 px-3">
                            <p className="text-sm text-slate-400">Logged in as</p>
                            <p className="text-white font-medium">{user.username}</p>
                        </div>
                    )}
                    <Button
                        variant="ghost"
                        onClick={handleLogout}
                        className={cn(
                            "w-full text-slate-400 hover:text-red-400 hover:bg-red-500/10",
                            sidebarOpen ? "justify-start" : "justify-center"
                        )}
                    >
                        <LogOut className="w-5 h-5" />
                        {sidebarOpen && <span className="ml-3">Logout</span>}
                    </Button>
                </div>
            </aside>

            {/* Main Content */}
            <main className={cn(
                "flex-1 transition-all duration-300",
                sidebarOpen ? "ml-64" : "ml-16"
            )}>
                <div className="p-6">
                    {children || <Outlet />}
                </div>
            </main>
        </div>
    );
}